import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { Capacitor } from '@capacitor/core';
import { GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { auth } from './index';
import { isEmbeddedBrowser } from './oauthEnv';

export const signInWithGooglePopup = async (): Promise<{
  idToken: string | null;
}> => {
  try {
    if (Capacitor.isNativePlatform()) {
      await FirebaseAuthentication.signInWithGoogle();
      // The backend verifies a Firebase-issued ID token (via Firebase Admin
      // SDK), not Google's own OAuth credential — getIdToken() is what
      // returns that, on both web and native.
      const { token } = await FirebaseAuthentication.getIdToken();

      if (!token) {
        throw new Error('No idToken returned from Google sign-in');
      }

      return { idToken: token };
    }

    // In-app browsers (Instagram, Facebook, Android WebViews…): Google refuses
    // OAuth there ("403 disallowed_useragent"), so a redirect would only land
    // on Google's error page. GoogleLoginButton catches this before calling
    // us and offers to reopen the page in Safari/Chrome (InAppBrowserDialog).
    if (isEmbeddedBrowser()) {
      throw Object.assign(new Error('Google sign-in is blocked in in-app browsers'), {
        code: 'auth/embedded-browser',
      });
    }

    // Every other browser, phones included: popup, opened straight from the
    // Firebase SDK. Two traps this avoids:
    // - redirect goes through <project>.firebaseapp.com, whose cross-origin
    //   storage Safari (ITP) and Chrome block, so the result is lost and the
    //   user lands back on /login signed out;
    // - the Capacitor plugin's web code is lazy-loaded, and that await before
    //   window.open makes iOS Safari block the popup. signInWithPopup opens the
    //   window synchronously, inside the tap.
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });
    const credential = await signInWithPopup(auth, provider);
    const token = await credential.user.getIdToken();

    if (!token) {
      throw new Error('No idToken returned from Google sign-in');
    }

    return { idToken: token };
  } catch (error) {
    console.error('Error during Google sign-in:', error);
    throw error;
  }
};
